import { LitElement, css, html } from 'lit'
import { customElement, property, queryAssignedElements, state } from 'lit/decorators.js'
import type { LinkedToDataType } from '../mixins/dom-linkable-mixin';
import { isNullishOrWhitespace } from '../functions/nullish';
import { PROXY_CONVERTERS } from '../directives/converters';

@customElement('ep-element-proxy')
export class EPElementProxy extends LitElement {
  @property({ attribute: 'proxy-format', reflect: true })
  proxyFormat: LinkedToDataType = 'string';

  @property({ attribute: 'proxy-property', reflect: true })
  proxyProperty: string = 'value';

  @property({ attribute: 'display-property', reflect: true })
  displayProperty: string = 'value';

  @property({ attribute: 'pull-events', reflect: true })
  pullEventsStr: string = '';

  get pullEvents(): string[] {
    return this.pullEventsStr.split(',').map(e => e.trim()).filter(e => !isNullishOrWhitespace(e));
  }

  @state()
  initialized: boolean = false;

  @property({ type: Boolean, attribute: 'debug', reflect: true })
  debug: boolean = false;

  //#region Slots
  @queryAssignedElements({ slot: 'display' })
  displayElements?: HTMLElement[];

  @queryAssignedElements({ slot: 'proxy' })
  proxyElements?: HTMLElement[];

  getDisplayElement(): HTMLElement | undefined {
    return (this.displayElements ?? [])[0];
  }

  getProxyElement(): HTMLElement | undefined {
    return (this.proxyElements ?? [])[0];
  }

  @state()
  currentDisplayElement?: HTMLElement = this.getDisplayElement();

  @state()
  currentProxyElement?: HTMLElement = this.getProxyElement();
  //#endregion
  
  render() {
    return html`
      <slot name="proxy" style="${this.debug ? "" : "display: none;"}" @slotchange=${this._handleProxySlotChange}></slot>
      <slot name="display" @slotchange=${this._handleDisplaySlotChange}></slot>
    `
  }

  private _handleProxySlotChange() {
    // store reference
    this.currentProxyElement = this.getProxyElement();
    // copy latest value to proxy
    this._readFromProxy();
  }

  private _handleDisplaySlotChange() {
    const displayElement = this.getDisplayElement();
    if (displayElement) {
      //console.log('handleDisplaySlotChange - setup listeners')
      // remove listeners from old element
      for(let eventName of this.pullEvents) {
        this.currentDisplayElement?.removeEventListener(eventName as any, this._writeToProxy.bind(this));
      }
      // set element as current element
      this.currentDisplayElement = displayElement
      // add listeners to new element
      for(let eventName of this.pullEvents) {
        this.currentDisplayElement.addEventListener(eventName as any, this._writeToProxy.bind(this));
      }
      // do first time callback with initial value
      if (!this.initialized) {
        this._readFromProxy();
        this.initialized = true;
      }
    }
  }

  private _writeToProxy() {
    const converter = PROXY_CONVERTERS[this.proxyFormat];
    if (this.currentDisplayElement && this.currentProxyElement && converter) {
      console.debug(`[${this.tagName}] Writing to proxy: <${this.currentDisplayElement.tagName}> ➡️ <${this.currentProxyElement.tagName}>`)
      try {
        // Get the value
        const nativeValue = this.currentDisplayElement[this.displayProperty as keyof HTMLElement];
        // Proxy converters is capable of encoding more than just strings, so use `as any`
        const proxyValue = converter.toAttribute(nativeValue as any)
        // @ts-ignore: We know TypeScript won't like this
        this.currentProxyElement[this.proxyProperty as keyof HTMLElement] = proxyValue
      }
      catch (err) {
        console.error('Oops!', err)
      }
    }
  }

  private _readFromProxy() {
    const converter = PROXY_CONVERTERS[this.proxyFormat];
    if (this.currentDisplayElement && this.currentProxyElement && converter) {
      console.debug(`[${this.tagName}] Reading from proxy: <${this.currentProxyElement.tagName}> ➡️ <${this.currentDisplayElement.tagName}>`)
      try {
        // Get the value from the proxy
        const proxyValue = this.currentProxyElement[this.proxyProperty as keyof HTMLElement];
        // Convert the proxy value
        const nativeValue = converter.fromAttribute(proxyValue as any)
        // @ts-ignore: We know TypeScript won't like this
        this.currentDisplayElement[this.displayProperty as keyof HTMLElement] = nativeValue
      }
      catch (err) {
        console.error('Oops!', err)
      }
    }
  }

  static styles = css`
    :host {
      display: contents;
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-element-proxy': EPElementProxy
  }
}
